import type { operations, paths } from "@airange/generated-contracts/sso-center";
import { ssoCenterOpenApiClient } from "../generated/sso-center/client";
import { omitEmptyQuery } from "./openapi-query";
import { unwrapOpenApiResult, unwrapOpenApiVoidResult } from "./openapi-result";
import { createRequestCache } from "./request-cache";

const PORTAL_GROUPS_PATH = "/api/identity/portal-groups" satisfies keyof paths;
const PORTAL_GROUP_PATH = "/api/identity/portal-groups/{group_key}" satisfies keyof paths;
const IDENTITY_SUMMARY_PATH = "/api/identity/summary" satisfies keyof paths;
const PORTAL_SETTINGS_PATH = "/api/identity/portal-settings" satisfies keyof paths;
const portalSettingsCache = createRequestCache<PortalSettings>({ ttlMs: 30_000 });

type PortalGroupsGet = operations["list_portal_groups_api_identity_portal_groups_get"];
type PortalGroupCreateOperation = operations["create_portal_group_api_identity_portal_groups_post"];
type PortalGroupUpdateOperation =
  operations["update_portal_group_api_identity_portal_groups__group_key__put"];
type PortalGroupDeleteOperation =
  operations["delete_portal_group_api_identity_portal_groups__group_key__delete"];
type IdentitySummaryGet = operations["get_identity_summary_api_identity_summary_get"];
type PortalSettingsGet = operations["get_portal_settings_api_identity_portal_settings_get"];
type PortalSettingsUpdateOperation =
  operations["update_portal_settings_api_identity_portal_settings_put"];

type PathParams<Operation> = Operation extends { parameters: { path: infer Params } }
  ? Params
  : never;

export type IdentitySummary = IdentitySummaryGet["responses"][200]["content"]["application/json"];

export type PortalSettings = PortalSettingsGet["responses"][200]["content"]["application/json"];

export type PortalBrandingSettings = PortalSettings["branding"];

export type PortalAccessSettings = PortalSettings["access"];

export type PortalSecuritySettings = PortalSettings["security"];

export type PortalGroup =
  PortalGroupCreateOperation["responses"][201]["content"]["application/json"];

export type PortalGroupListQuery = NonNullable<PortalGroupsGet["parameters"]["query"]>;

export type PortalGroupCreatePayload =
  PortalGroupCreateOperation["requestBody"]["content"]["application/json"];

export type PortalGroupUpdatePayload =
  PortalGroupUpdateOperation["requestBody"]["content"]["application/json"];

type PortalGroupPathParams = PathParams<PortalGroupUpdateOperation> &
  PathParams<PortalGroupDeleteOperation>;

export function listPortalGroups(query?: PortalGroupListQuery) {
  return unwrapOpenApiResult(
    ssoCenterOpenApiClient.GET(PORTAL_GROUPS_PATH, {
      params: {
        query: omitEmptyQuery(query),
      },
    }),
  );
}

export function createPortalGroup(payload: PortalGroupCreatePayload) {
  return unwrapOpenApiResult(
    ssoCenterOpenApiClient.POST(PORTAL_GROUPS_PATH, {
      body: payload,
    }),
  );
}

export function updatePortalGroup(groupKey: string, payload: PortalGroupUpdatePayload) {
  const path: PortalGroupPathParams = {
    group_key: groupKey,
  };

  return unwrapOpenApiResult(
    ssoCenterOpenApiClient.PUT(PORTAL_GROUP_PATH, {
      params: {
        path,
      },
      body: payload,
    }),
  );
}

export function deletePortalGroup(groupKey: string) {
  const path: PortalGroupPathParams = {
    group_key: groupKey,
  };

  return unwrapOpenApiVoidResult(
    ssoCenterOpenApiClient.DELETE(PORTAL_GROUP_PATH, {
      params: {
        path,
      },
    }),
  );
}

export type PortalSettingsUpdatePayload =
  PortalSettingsUpdateOperation["requestBody"]["content"]["application/json"];

export function getIdentitySummary() {
  return unwrapOpenApiResult(ssoCenterOpenApiClient.GET(IDENTITY_SUMMARY_PATH));
}

export function getPortalSettings(options?: { forceRefresh?: boolean }) {
  return portalSettingsCache.get(
    () => unwrapOpenApiResult(ssoCenterOpenApiClient.GET(PORTAL_SETTINGS_PATH)),
    options?.forceRefresh,
  );
}

export async function updatePortalSettings(payload: PortalSettingsUpdatePayload) {
  const result = await unwrapOpenApiResult(
    ssoCenterOpenApiClient.PUT(PORTAL_SETTINGS_PATH, {
      body: payload,
    }),
  );
  portalSettingsCache.invalidate();
  return result;
}
